/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';

import { useHQVStore, HQVStore } from '../../modules/store/hqvZustandStore';
import { HQVContext, HQVContextType } from '../../context/HQVContext';
import { HQVModalFlagContext, HQVModalFlagType } from '../../context/HQVModalFlagContext';

interface UseConditionalHQVContextOptions {
  /**
   * Specify which page/flag this consumer belongs to so the correct feature flag is checked.
   * - 'serp' -> EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND (SERP results, HQVWrapper SWR path)
   * - 'adf'  -> EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND (Availability Calendar / SearchForm ADF legacy path)
   *
   * Shared modal readers (HotelOverview/*, HotelInformation/*, HotelGallery*) do NOT know which
   * page mounted them, so they omit this option. In that case the hook reads flagType from
   * HQVModalFlagContext, which the modal owner (SERP results / ADF SearchForm / HQVWrapper) sets
   * around <PageonModal>. If no provider is present (unusual outside the modal), we default to 'serp'.
   */
  flagType?: HQVModalFlagType;
}

/**
 * Hook that conditionally returns HQV state from either the Zustand store or React Context
 * based on ONE page-specific feature flag, matching the useConditionalPageContext pattern
 * used across the codebase.
 *
 * Important: this hook checks ONLY the flag for its owning page. It does NOT OR-combine
 * SERP_ZUSTAND and ADF_ZUSTAND, so toggling one flag can no longer leak into the other page.
 */
export const useConditionalHQVContext = (
  options: UseConditionalHQVContextOptions = {}
): HQVContextType & Partial<Omit<HQVStore, keyof HQVContextType>> => {
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  const modalFlagFromContext = useContext(HQVModalFlagContext);

  const resolvedFlagType: HQVModalFlagType = options.flagType ?? modalFlagFromContext ?? 'serp';

  const shouldUseZustand =
    resolvedFlagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  const contextValue = useContext(HQVContext);

  const hqvState = useHQVStore(state => state.hqvState);
  const hqvDispatch = useHQVStore(state => state.hqvDispatch);
  const setAmenetiesAvailable = useHQVStore(state => state.setAmenetiesAvailable);
  const setPhotosAvailable = useHQVStore(state => state.setPhotosAvailable);
  const setPropertyDetailsAvailable = useHQVStore(state => state.setPropertyDetailsAvailable);
  const setInformationAvailable = useHQVStore(state => state.setInformationAvailable);
  const setPropertyID = useHQVStore(state => state.setPropertyID);
  const setModalOpenState = useHQVStore(state => state.setModalOpenState);
  const setOpenGalleryOnLoad = useHQVStore(state => state.setOpenGalleryOnLoad);
  const setPropertySessionData = useHQVStore(state => state.setPropertySessionData);
  const setPropertyDistance = useHQVStore(state => state.setPropertyDistance);
  const setCachedPropertyMedia = useHQVStore(state => state.setCachedPropertyMedia);

  if (shouldUseZustand) {
    return {
      hqvState,
      hqvDispatch,
      setAmenetiesAvailable,
      setPhotosAvailable,
      setPropertyDetailsAvailable,
      setInformationAvailable,
      setPropertyID,
      setModalOpenState,
      setOpenGalleryOnLoad,
      setPropertySessionData,
      setPropertyDistance,
      setCachedPropertyMedia,
    };
  }

  return contextValue;
};

/**
 * Convenience wrapper for SERP-side consumers (SearchResultsMainComponent, PropertyCardV0,
 * hotelCard, imageCarousel NoImgDiv, Distance, HQVWrapper).
 */
export const useConditionalHQVContextSERP = () => useConditionalHQVContext({ flagType: 'serp' });

/**
 * Convenience wrapper for ADF-side consumers (SearchFormComponent when enableAdfSearchForm is true).
 */
export const useConditionalHQVContextADF = () => useConditionalHQVContext({ flagType: 'adf' });
